/**
 * A 24-cell heat strip for one activity kind: one cell per local hour of the day, coloured by
 * whether the hour suits the kind (primary) or does not (destructive), with opacity carrying how
 * much evidence backs it, so a confident hour reads as solid and a guess as faint. Drawn as SVG
 * with presentation attributes only (no inline styles), so it scales and respects the CSP.
 *
 * @module apps/web/src/components/flux-agent/timing/TimingHeatStrip
 */

import type { FluxAgentTimingHour } from '@/lib/fluxAgentTimingApi';

/** Width of one hour cell in SVG units. */
const CELL = 10;
/** Height of the strip in SVG units, excluding the tick labels. */
const HEIGHT = 12;
/** Hours that get a tick label. */
const TICKS: [number, string][] = [
  [0, '12a'],
  [6, '6a'],
  [12, '12p'],
  [18, '6p'],
];

/** Inputs for {@link TimingHeatStrip}. */
export interface TimingHeatStripProps {
  /** 24 hours, local midnight first. */
  hours: FluxAgentTimingHour[];
  /** Kind label used in the accessible description. */
  label: string;
}

/** Opacity of one cell: confidence times a floor-lifted magnitude, so weak values stay visible when confident. */
export function cellOpacity(hour: FluxAgentTimingHour): number {
  const confidence = Math.min(1, Math.max(0, hour.confidence));
  const magnitude = Math.min(1, Math.abs(hour.value));
  return Math.round(confidence * (0.3 + 0.7 * magnitude) * 100) / 100;
}

/** Plain-language summary of the hours for screen readers. */
function describe(hours: FluxAgentTimingHour[], label: string): string {
  const good = hours.flatMap((hour, index) =>
    hour.value > 0.15 && hour.confidence > 0.2 ? [index] : []
  );
  const hard = hours.flatMap((hour, index) =>
    hour.value < -0.15 && hour.confidence > 0.2 ? [index] : []
  );
  const clock = (hour: number) => `${hour % 12 === 0 ? 12 : hour % 12}${hour < 12 ? 'am' : 'pm'}`;
  const list = (indexes: number[]) => indexes.map(clock).join(', ');
  const parts = [
    good.length ? `suits ${label.toLowerCase()} around ${list(good)}` : '',
    hard.length ? `harder around ${list(hard)}` : '',
  ].filter(Boolean);
  return `Hours of the day for ${label}: ${parts.length ? parts.join('; ') : 'no clear pattern yet'}.`;
}

/** Draw one kind's 24 hours as a heat strip with confidence opacity. */
export function TimingHeatStrip({ hours, label }: TimingHeatStripProps) {
  return (
    <svg
      viewBox={`0 0 ${CELL * 24} ${HEIGHT + 11}`}
      className="h-auto w-full max-w-xs"
      role="img"
      aria-label={describe(hours, label)}
      data-testid="timing-heat-strip"
    >
      <rect
        x="0"
        y="0"
        width={CELL * 24}
        height={HEIGHT}
        rx="2"
        className="text-muted-foreground"
        fill="currentColor"
        fillOpacity="0.08"
      />
      {hours.slice(0, 24).map((hour, index) => (
        <rect
          key={index}
          x={index * CELL + 0.5}
          y="0"
          width={CELL - 1}
          height={HEIGHT}
          rx="1.5"
          fill="currentColor"
          fillOpacity={cellOpacity(hour)}
          className={hour.value < 0 ? 'text-destructive' : 'text-primary'}
          data-hour={index}
        />
      ))}
      {TICKS.map(([hour, text]) => (
        <text
          key={hour}
          x={hour * CELL + 1}
          y={HEIGHT + 9}
          fontSize="7"
          fill="currentColor"
          className="text-muted-foreground"
        >
          {text}
        </text>
      ))}
    </svg>
  );
}
